import type { Node as PMNode } from '@tiptap/pm/model';
import { diffWords } from '../extensions/ai-editing/diff';

export const BLOCK_SEPARATOR = '\n' as const;

type TextRange = { start: number; end: number };
type PositionRange = { from: number; to: number };
type Chunk = { offset: number; pos: number; length: number };

export function addedTextRanges(before: string, after: string): TextRange[] {
  const ranges: TextRange[] = [];
  let cursor = 0;
  for (const segment of diffWords(before, after)) {
    if (segment.type === 'added') {
      const leading = segment.after.length - segment.after.trimStart().length;
      const start = cursor + leading;
      const end = cursor + segment.after.trimEnd().length;
      if (end > start) ranges.push({ start, end });
    }
    cursor += segment.after.length;
  }
  return ranges;
}

export interface RemovedRun { at: number; text: string; block: boolean }

export function removedTextRuns(before: string, after: string): RemovedRun[] {
  const runs: RemovedRun[] = [];
  let beforeCursor = 0;
  let afterCursor = 0;
  for (const segment of diffWords(before, after)) {
    const s = beforeCursor;
    const e = beforeCursor + segment.before.length;
    if (segment.type === 'removed') {
      const startsLine = s === 0 || before[s - 1] === '\n';
      const endsLine = e === before.length || before[e - 1] === '\n';
      const block = startsLine && endsLine;
      const trimmed = segment.before.trim();
      if (trimmed) {
        const movesBack =
          !block && before[e - 1] === '\n' && !startsLine && after[afterCursor - 1] === '\n';
        runs.push({
          at: movesBack ? afterCursor - 1 : afterCursor,
          text: block ? trimmed : trimmed.replace(/\s*\n\s*/g, ' '),
          block,
        });
      }
    }
    beforeCursor = e;
    afterCursor += segment.after.length;
  }
  return runs;
}

function collectChunks(doc: PMNode): Chunk[] {
  const chunks: Chunk[] = [];
  let offset = 0;
  let separated = true;
  doc.descendants((node, pos) => {
    const leafText = node.isLeaf && !node.isText ? (node.type.spec.leafText?.(node) ?? '') : '';
    if (node.isBlock && ((node.isLeaf && leafText) || node.isTextblock)) {
      if (separated) separated = false;
      else offset += BLOCK_SEPARATOR.length;
    }
    if (node.isText) {
      const length = node.text?.length ?? 0;
      chunks.push({ offset, pos, length });
      offset += length;
    } else {
      offset += leafText.length;
    }
  });
  return chunks;
}

export function textOffsetToPosition(doc: PMNode, offset: number): number | null {
  const chunks = collectChunks(doc);
  const containing = chunks.find((c) => offset >= c.offset && offset <= c.offset + c.length);
  if (containing) return containing.pos + (offset - containing.offset);
  const before = chunks.filter((c) => c.offset + c.length < offset).pop();
  if (before) return before.pos + before.length;
  return chunks[0]?.pos ?? null;
}

export function textOffsetToBlockPosition(doc: PMNode, offset: number): number {
  if (offset >= doc.textBetween(0, doc.content.size, BLOCK_SEPARATOR).length) {
    return doc.content.size;
  }
  const pos = textOffsetToPosition(doc, offset);
  if (pos === null) return doc.content.size;
  const $pos = doc.resolve(pos);
  let depth = $pos.depth;
  while (depth > 1 && $pos.index(depth - 1) === 0) depth -= 1;
  return $pos.before(depth);
}

export function textRangesToPositions(doc: PMNode, ranges: TextRange[]): PositionRange[] {
  const chunks = collectChunks(doc);
  const positions: PositionRange[] = [];
  for (const range of ranges) {
    for (const chunk of chunks) {
      const start = Math.max(range.start, chunk.offset);
      const end = Math.min(range.end, chunk.offset + chunk.length);
      if (end > start) {
        positions.push({
          from: chunk.pos + (start - chunk.offset),
          to: chunk.pos + (end - chunk.offset),
        });
      }
    }
  }
  return positions;
}
